import { ActivityResultWrapper } from '@components';
import { UrlUtils, Responsive, AppDispatch, RootState } from '@share/utils';
import './style.scss';
import { ActivitySearchWrapper } from '@share/components';
import { isEmpty } from 'lodash';

import { IActivityState } from '@share/store/slices';
import { useDispatch, useSelector } from 'react-redux';
import { ActivitySkeletonResult } from '@components';
import { IActivityLocationsState, IDatesState, getActivity, activityFiltersActions } from '@share/store/slices';
import { useCallback, useEffect, useRef } from 'react';
import { isDataValid } from '@share/utils';
import { FILTERS_LABEL } from '@constants';

/**
 * Activity search page section (TRAV23 activities-front merge).
 *
 * Owns the whole results area: runs the initial search once the URL carries a valid
 * destination, then shows the skeleton while loading and the results once they land.
 *
 * Not to be confused with `ActivitySearchForm` (`@share/components/activity-search`),
 * which is just the destination/date/occupancy fields. Both were called
 * `SearchActivity` in the activities front, in different folders.
 */

interface IProps {
  onSearch?: () => void;
}

export const ActivitySearchSection: React.FC<IProps> = ({ onSearch }) => {

  const { activities, loading, error, searchedLocation }: IActivityState = useSelector((state: RootState) => state.activityStore);
  const values = UrlUtils.getValues();
  const activityLocationsStore: IActivityLocationsState = useSelector((state: RootState) => state.activityLocationsStore);
  const datesStore: IDatesState = useSelector((state: RootState) => state.datesStore);
  const account = useSelector((state: RootState) => state.loginStore.account);
  const dispatch = useDispatch<AppDispatch>();
  const hasRefCalledInitialSearch = useRef(false);

  const search = useCallback(() => {
    const { isValid } = isDataValid(activityLocationsStore, datesStore, { requireDates: false });

    if (!isValid) return;

    dispatch(
      getActivity({
        isLoadMore: false,
        isFiltersChange: false,
        isReuseSessionKey: false,
      })
    );
    if (onSearch) onSearch();
  }, [activityLocationsStore, datesStore, dispatch, onSearch]);

  useEffect(() => {
    const { isValid } = isDataValid(activityLocationsStore, datesStore, { requireDates: false });
    if (isValid && !hasRefCalledInitialSearch.current && !loading) {
      if (!values[FILTERS_LABEL]) {
        dispatch(activityFiltersActions.resetFilters(account?.isCapitalVacations));
      }
      search();
      hasRefCalledInitialSearch.current = true;
    }
  }, [activityLocationsStore, datesStore, search, loading, dispatch, account, values]);

  return (
    <div className="search-activity">
      <div className='activity-search-wrapper after-search' style={{ backgroundImage: 'url("/src/assets-share/images/hotel/bg-hotels.png")' }}>
        <ActivitySearchWrapper onSearch={search} />
      </div>

      {/* Skeleton loader */}
      {activities && loading && isEmpty(error) ? (
        <ActivitySkeletonResult label={searchedLocation} />
      ) : null}

      {!isEmpty(values) && !loading && (activities || !isEmpty(error)) ? (
        <Responsive>
          <ActivityResultWrapper />
        </Responsive>
      ) : null}
    </div>
  );
};
